import React, { useEffect, useMemo, useRef } from "react";
import {
  View,
  Text,
  StyleSheet,
  Platform,
  Animated,
  Easing,
  useWindowDimensions,
} from "react-native";
import { getScaledValue } from "@rnv/renative";
import { VideoDetail } from "../../api/content";
import VideoPlayer from "../../components/VideoPlayer";
import Mascot from "../../components/Mascot";
import { fontFamily } from "../../theme";

type Props = {
  video: VideoDetail;
  elapsed: number;
  paused?: boolean;
  onTimeUpdate?: (seconds: number) => void;
  onEnded?: () => void;
};

// Bedtime category: dark calm visuals, audio keeps playing via an off-screen
// VideoPlayer. The visible UI is starfield + sleeping mascot + waveform +
// "time left" pill. Layout avoids flex `gap` and `aspect-ratio` because TV
// targets (Tizen/WebOS) are pinned to Chromium 76.

// Bar heights lifted straight from the design handoff to keep visual parity.
const BAR_HEIGHTS = [
  14, 22, 30, 18, 42, 28, 36, 20, 44, 24, 32, 18, 28, 22, 16,
];
const STAR_COUNT = 36;

// Deterministic pseudo-random star layout — mirrors the design's
// `(i*73)%100` / `(i*41)%70` trick. Computed once at module load so every
// render is identical.
type Star = { left: string; top: string; opacity: number; period: number };
const STARS: Star[] = Array.from({ length: STAR_COUNT }, (_, i) => ({
  left: `${(i * 73) % 100}%`,
  top: `${(i * 41) % 70}%`,
  opacity: 0.3 + (i % 4) * 0.18,
  period: 2000 + (i % 5) * 1000,
}));

function PulseStar({ star }: { star: Star }) {
  const v = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    const anim = Animated.loop(
      Animated.sequence([
        Animated.timing(v, {
          toValue: 1,
          duration: star.period / 2,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
        Animated.timing(v, {
          toValue: 0,
          duration: star.period / 2,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
      ]),
    );
    anim.start();
    return () => anim.stop();
  }, [v, star.period]);
  const opacity = v.interpolate({
    inputRange: [0, 1],
    outputRange: [star.opacity * 0.5, star.opacity],
  });
  return (
    <Animated.View
      style={{
        position: "absolute",
        left: star.left as any,
        top: star.top as any,
        width: 2,
        height: 2,
        borderRadius: 1,
        backgroundColor: "#FFFFFF",
        opacity,
      }}
    />
  );
}

function WaveBar({ height, index }: { height: number; index: number }) {
  const v = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    const duration = 1200 + (index % 3) * 400;
    const delay = index * 60;
    const anim = Animated.loop(
      Animated.sequence([
        Animated.delay(delay),
        Animated.timing(v, {
          toValue: 1,
          duration: duration / 2,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
        Animated.timing(v, {
          toValue: 0,
          duration: duration / 2,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
      ]),
    );
    anim.start();
    return () => anim.stop();
  }, [v, index]);
  const scaleY = v.interpolate({
    inputRange: [0, 1],
    outputRange: [0.55, 1],
  });
  const baseOpacity = 0.7 + (index % 3) * 0.1;
  return (
    <Animated.View
      style={{
        width: getScaledValue(4),
        height: getScaledValue(height),
        marginHorizontal: getScaledValue(2),
        backgroundColor: "#FFE9B0",
        borderRadius: 2,
        opacity: baseOpacity,
        transform: [{ scaleY }],
      }}
    />
  );
}

export default function VoiceBedtimeScreen({
  video,
  elapsed,
  paused,
  onTimeUpdate,
  onEnded,
}: Props) {
  const dims = useWindowDimensions();
  const winW = dims.width || (typeof window !== "undefined" ? window.innerWidth : 0);
  const winH = dims.height || (typeof window !== "undefined" ? window.innerHeight : 0);
  // See PlayerScreen: flex:1 collapses the audio-only stage to ~0 height on
  // Tizen/webOS, and an explicit height is ignored unless flex is neutralized
  // (RN-web's flex:1 → flex-basis:0%). flex 0/0/auto makes width/height real.
  const fill = Platform.OS === "web"
    ? { width: winW, height: winH, flexGrow: 0, flexShrink: 0, flexBasis: "auto" as const }
    : null;

  // Subtitle: prefer the first non-bedtime category capitalized, else fall
  // back to a friendly default. The design's "Chapter 3 · The Sleepy Cloud"
  // string isn't in the data model so we approximate.
  const subtitle = useMemo(() => {
    const cats = video.categories ?? [];
    const tag = cats.find((c) => c.toLowerCase() !== "bedtime") ?? "bedtime";
    return `Bedtime story · ${tag.charAt(0).toUpperCase()}${tag.slice(1)}`;
  }, [video.categories]);

  const minutesLeft = useMemo(() => {
    const remaining = Math.max(0, (video.duration ?? 0) - elapsed);
    return Math.floor(remaining / 60);
  }, [video.duration, elapsed]);

  // On web (Tizen/WebOS), render the radial gradient via inline CSS —
  // RN-Web supports `backgroundImage`. Native gets a flat deep-purple fallback
  // since expo-linear-gradient isn't installed and we don't want a new dep.
  const stageStyle = useMemo(() => {
    if (Platform.OS === "web") {
      // RN-Web honors the CSS `backgroundImage` string even though the
      // RN type doesn't list it. Cast to any to keep tsc quiet.
      return {
        ...styles.stage,
        backgroundImage:
          "radial-gradient(60% 80% at 50% 60%, #4A3F75 0%, #1F1B36 100%)",
        backgroundColor: "#1F1B36",
      } as any;
    }
    return [styles.stage, { backgroundColor: "#2A2548" }];
  }, []);

  return (
    <View style={[styles.root, fill]}>
      {/* Hidden audio source — the visual is zero-sized but the HLS keeps
          playing. Native <Video> and the web <video> element both continue
          delivering audio when their parent is sized 1×1 with opacity 0; the
          element is mounted, just imperceptible. */}
      <View pointerEvents="none" style={styles.hiddenAudio}>
        <VideoPlayer
          hlsUrl={video.hlsUrl}
          mimeType={video.videoMimeType}
          onTimeUpdate={onTimeUpdate}
          onEnded={onEnded}
          paused={paused}
        />
      </View>

      <View style={[stageStyle, fill] as any}>
        {STARS.map((star, i) => (
          <PulseStar key={i} star={star} />
        ))}
        <View style={styles.center}>
          <View style={styles.mascotWrap}>
            <Mascot
              mood="sleep"
              color="rgba(255,255,255,0.85)"
              size={getScaledValue(180)}
              bob
            />
          </View>
          <Text style={styles.title} numberOfLines={2}>
            {video.title}
          </Text>
          <Text style={styles.subtitle} numberOfLines={1}>
            {subtitle}
          </Text>
          <View style={styles.wave}>
            {BAR_HEIGHTS.map((h, i) => (
              <WaveBar key={i} height={h} index={i} />
            ))}
          </View>
          <View style={styles.pill}>
            <Text style={styles.pillText}>
              {`Screen rests · audio only · ${minutesLeft} min left`}
            </Text>
          </View>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: "#1F1B36" },
  // Offscreen 1×1 keeps audio alive without ever painting visible video.
  hiddenAudio: {
    position: "absolute",
    width: 1,
    height: 1,
    opacity: 0,
    top: 0,
    left: 0,
  },
  stage: {
    flex: 1,
    position: "relative",
    overflow: "hidden",
  },
  center: {
    position: "absolute",
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    alignItems: "center",
    justifyContent: "center",
  },
  mascotWrap: {
    alignItems: "center",
    justifyContent: "center",
  },
  title: {
    color: "#FFFFFF",
    fontFamily,
    fontSize: getScaledValue(44),
    fontWeight: "900",
    letterSpacing: -1,
    marginTop: getScaledValue(30),
    textAlign: "center",
    paddingHorizontal: getScaledValue(40),
  },
  subtitle: {
    color: "#FFFFFF",
    fontFamily,
    fontSize: getScaledValue(22),
    fontWeight: "700",
    opacity: 0.7,
    marginTop: getScaledValue(8),
    textAlign: "center",
  },
  // No flex `gap` here — TV Chromium 76 floor. We use marginHorizontal on
  // each bar instead.
  wave: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    height: getScaledValue(48),
    marginTop: getScaledValue(30),
  },
  pill: {
    marginTop: getScaledValue(26),
    paddingHorizontal: getScaledValue(14),
    paddingVertical: getScaledValue(8),
    borderRadius: 999,
    backgroundColor: "rgba(255,255,255,0.10)",
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.15)",
  },
  pillText: {
    color: "#FFFFFF",
    fontFamily,
    fontSize: getScaledValue(14),
    fontWeight: "700",
    opacity: 0.85,
  },
});
